import { Formik, useFormikContext } from "formik";
import { toast } from "react-toastify";
import { Button, Form } from "react-bootstrap";
import { OctTable } from "@oceantech/oceantech-ui";
import { CODE, WAREHOUSE_ID } from "../../utils/Constant";
import { formatMoney } from "../../utils/FormatUtils";
import { AppContext } from "../../appContext/AppContext";
import DynamicComponent from "../../component/GenerateDynamicComponent";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import GenerateFormComponent from "../../component/generate-form/GenerateFormHTMLNoValid";
import { generateForm, generateInitValues, generateValidationSchema, roundToDecimalPlaces } from "../../utils/AppFunction";
import { searchListItemGoiThau } from "../DanhMucServices";
import { importDSVatTuDuTruBoSung } from "../../phan-he-tu-truc/components/DuTruBoSungVatTu/services";
import TextField from "../../component/TextField";
import moment from "moment";

interface Props {
  isView?: boolean;
  type: any;
  dataEdit: any;
  lstVatTu: any[];
  indexRowEdit: number | null;
  setLstVatTu: Dispatch<SetStateAction<any[]>>;
  setIndexRowEdit: Dispatch<SetStateAction<number | null>>;
  warehouseId?: number;
  handleDownloadSampleImport?: () => void;
}

interface PropsVatTu {
  modelID: string
}

const HandleValues = (props: PropsVatTu) => {
  const { modelID } = props;
  const { values, setFieldValue } = useFormikContext<any>();
  const data = values[modelID];

  const handleTotalAmount = (quantity: number, price: number) => {
    const result = quantity * price;
    return roundToDecimalPlaces(result);
  };

  useEffect(() => {
    setFieldValue(modelID, {
      ...data,
      totalAmount: handleTotalAmount(data.totalQuantity, data.sellingPrice),
    })
  }, [data?.totalQuantity, data?.sellingPrice]);


  return <></>
};

const FormVatTuDanhMuc = (props: Props) => {
  const {
    isView,
    type,
    dataEdit,
    lstVatTu,
    setLstVatTu,
    indexRowEdit,
    setIndexRowEdit,
    warehouseId = WAREHOUSE_ID.NHA_THUOC,
    handleDownloadSampleImport,
  } = props;
  const isSiteRHM = process.env.REACT_APP_SITE === 'RHM';
  const labelProduct = isSiteRHM ? 'hàng hóa' : 'Thuốc/VT';
  const { setIsLoading } = useContext(AppContext);
  const [listJsonGenerate, setListJsonGenerate] = useState<any>([]);
  const [initValues, setInitValues] = useState<any>({});

  const updateForm = async () => {
    const data = await generateForm(type?.description?.formVatTu, setIsLoading);
    setInitValues(generateInitValues(data));
    setListJsonGenerate(data || []);
  };

  const hasDuplicate = (lst: any[], indexRowEdit: number | null) => {
    const seen = new Map();
    for (let i = 0; i < lst.length; i++) {
      const item = lst[i];
      const key = `${item.itemId}-${item.supplier}`;
      // Nếu key đã tồn tại trong Map
      if (seen.has(key)) {
        const existingIndex = seen.get(key);
        // Bỏ qua nếu chỉ số hiện tại bằng indexRowEdit
        if (existingIndex !== indexRowEdit) {
          return true; // Có item trùng itemId và supplier
        }
      }
      // Lưu key và index vào Map
      seen.set(key, i);
    }

    return false; // Không có item nào trùng itemId và supplier
  };

  const handleAddItem = (values: any) => {
    const dataItem = values[type?.description?.formVatTu] || {};
    const errrorMessage = !dataItem.code
      ? "Chưa chọn thuốc/VT"
      : !dataItem.totalQuantity
        ? "Chưa nhập số lượng"
        : parseFloat(dataItem.totalQuantity) <= 0
          ? "Số lượng phải lớn hơn 0"
          : hasDuplicate([...lstVatTu, dataItem], indexRowEdit)
            ? "Vật tư và nhà cung cấp đã tồn tại"
            : null;

    if (errrorMessage) {
      toast.warning(errrorMessage);
      return;
    }

    let newLstItem = [...lstVatTu];
    if (indexRowEdit === null) {
      newLstItem = [
        ...newLstItem,
        {
          ...dataItem,
          initialQuantity: dataItem.totalQuantity,
          id: null,
          itemId: dataItem.itemId,
          warehouseId: warehouseId,
        },
      ];
    } else {
      if (newLstItem[indexRowEdit]?.isUsed) {
        toast.warning("Thuốc/vật tư đã phát sinh nhập kho, không thể chỉnh sửa!");
        return;
      }
      newLstItem[indexRowEdit] = {
        ...dataItem,
        initialQuantity: dataItem.totalQuantity,
        id: newLstItem[indexRowEdit]?.id,
        itemId: dataItem.itemId || dataItem.item?.id,
        warehouseId: warehouseId,
      };
    }
    setIndexRowEdit(null);
    setInitValues(generateInitValues(listJsonGenerate));
    setLstVatTu(newLstItem);
  };

  const handleEditItem = (item: any, index: number) => {
    if (item?.isUsed) {
      toast.warning("Thuốc/vật tư đã phát sinh nhập kho, không thể chỉnh sửa!");
      return;
    }
    setIndexRowEdit(index);
    setInitValues({ ...initValues, ...item });
  };

  const handleDeleteItem = (index: number) => {
    if (lstVatTu[index]?.isUsed) {
      toast.warning("Thuốc/vật tư đã phát sinh nhập kho, không thể xóa!");
      return;
    }
    const data = [...lstVatTu].filter((_: any, i: number) => i !== index);
    setLstVatTu(data);
  };

  const handleGetDetail = async () => {
    try {
      setIsLoading(true);
      const { data } = await searchListItemGoiThau(dataEdit.id);
      if (data.code === CODE.SUCCESS) {
        setLstVatTu(data.data.content);
      } else {
        toast.error('Lỗi tải danh sách vật tư, vui lòng thử lại');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    type.description?.formVatTu && updateForm();
  }, [type.description?.formVatTu]);

  useEffect(() => {
    dataEdit?.id && handleGetDetail();
  }, [dataEdit?.id]);

  const columns = [
    {
      name: "STT",
      field: "",
      headerStyle: {
        minWidth: 50,
        maxWidth: 50,
      },
      cellStyle: {
        textAlign: "center",
      },
      render: (row: any, index: number, stt: number) => stt,
    },
    ...(!isView
      ? [
        {
          name: "TT",
          field: "",
          headerStyle: {
            minWidth: 80,
            maxWidth: 80,
          },
          cellStyle: {
            textAlign: "center",
          },
          render: (row: any, index: number, stt: number) => {
            const isUsed = Boolean(row?.isUsed);
            return (
              <div className="d-flex justify-content-center">
                {indexRowEdit !== index && (
                  <>
                    <i
                      onClick={(e) => {
                        if (isUsed) {
                          toast.warning("Thuốc/vật tư đã phát sinh nhập kho, không thể chỉnh sửa!");
                          return;
                        }
                        handleEditItem(row, index);
                      }}
                      className={`bi bi-pencil-square ${isUsed ? 'text-muted opacity-50' : 'text-navy cursor-pointer'}`}
                      style={{ cursor: isUsed ? 'not-allowed' : 'pointer' }}
                      title={isUsed ? "Thuốc đã phát sinh nhập kho (Không thể sửa)" : "Sửa"}
                    ></i>
                    <i
                      onClick={(e) => {
                        if (isUsed) {
                          toast.warning("Thuốc/vật tư đã phát sinh nhập kho, không thể xóa!");
                          return;
                        }
                        handleDeleteItem(index);
                      }}
                      className={`bi bi-trash3-fill spaces ml-10 ${isUsed ? 'text-muted opacity-50' : 'text-danger cursor-pointer'}`}
                      style={{ cursor: isUsed ? 'not-allowed' : 'pointer' }}
                      title={isUsed ? "Thuốc đã phát sinh nhập kho (Không thể xóa)" : "Xóa"}
                    ></i>
                  </>
                )}
              </div>
            );
          },
        },
      ]
      : []),
    {
      name: isSiteRHM ? 'Mã kế toán' : 'Mã thuốc thông tư',
      field: "code",
      headerStyle: {
        minWidth: isSiteRHM ? 120 : 200,
      },
      cellStyle: {
        textAlign: "left",
      },
      render: (row: any, index: number, stt: number) =>
        row.code || row.item?.code || "",
    },
    {
      name: 'Mã thuốc bệnh viện',
      field: "name",
      headerStyle: {
        minWidth: 200,
      },
      cellStyle: {
        textAlign: "left",
      },
      render: (row: any, index: number, stt: number) =>
        row?.hospitalMedicineCode || row.item?.hospitalMedicineCode || "",
    },
    ...(isSiteRHM ? [{
      name: 'Mã hàng hóa NCC',
      field: "supplierItemCode",
      headerStyle: {
        minWidth: 140,
      },
      cellStyle: {
        textAlign: "left",
      },
      render: (row: any, index: number, stt: number) =>
        row.supplierItemCode || row.item?.supplierItemCode || "",
    }] : []),
    {
      name: isSiteRHM ? 'Tên hàng hóa' : 'Tên thuốc/Vật tư',
      field: "name",
      headerStyle: {
        minWidth: 200,
      },
      cellStyle: {
        textAlign: "left",
      },
      render: (row: any, index: number, stt: number) =>
        row.name || row.item?.name || "",
    },
    {
      name: "ĐVT",
      field: "unitofMeasureName",
      headerStyle: {
        minWidth: 100,
        maxWidth: 100,
      },
      cellStyle: {
        textAlign: "left",
      },
      render: (row: any, index: number, stt: number) =>
        row.unitofMeasureName || row.item?.unitofMeasureName || "",
    },
    {
      name: "SL thầu",
      field: "initialQuantity",
      headerStyle: {
        minWidth: 100,
      },
      cellStyle: {
        textAlign: "right",
      },
      render: (row: any, index: number, stt: number) =>
        formatMoney(row?.initialQuantity),
    },
    {
      name: "SL còn lại",
      field: "totalQuantity",
      headerStyle: {
        minWidth: 100,
      },
      cellStyle: {
        textAlign: "right",
      },
      render: (row: any, index: number, stt: number) =>
        formatMoney(row?.totalQuantity),
    },
    {
      name: "Tiến độ (%)",
      field: "percentDone",
      headerStyle: {
        minWidth: 100,
      },
      cellStyle: {
        textAlign: "right",
      },
      render: (row: any, index: number, stt: number) => (row?.percentDone || 0) + "%"
    },
    {
      name: "Giá thầu",
      field: "sellingPrice",
      headerStyle: {
        minWidth: 110,
      },
      cellStyle: {
        textAlign: "right",
      },
      render: (row: any, index: number, stt: number) =>
        formatMoney(row?.sellingPrice) || "",
    },
    {
      name: "Thành tiền",
      field: "totalAmount",
      headerStyle: {
        minWidth: 110,
      },
      cellStyle: {
        paddingRight: "10px",
        textAlign: "right",
      },
      render: (row: any, index: number, stt: number) =>
        formatMoney(row?.totalAmount) || "",
    },
    {
      name: "Gói thầu",
      field: "biddingPackage",
      headerStyle: {
        minWidth: 110,
      },
      cellStyle: {
        paddingRight: "10px",
        textAlign: "left",
      },
    },
      {
      name: "Nhóm thầu",
      field: "biddingGroup",
      headerStyle: {
        minWidth: 80,
      },
      cellStyle: {
        paddingRight: "10px",
        textAlign: "center",
      },
    },
    {
      name: "Nhà cung cấp",
      field: "supplierName",
      headerStyle: {
        minWidth: 260,
      },
      cellStyle: {
        paddingRight: "10px",
        textAlign: "right",
      },
    },
  ];

  const handleSelectFileImport = async (e: any) => {
    if (!e.target.files[0]) return;
    try {
      setIsLoading(true);
      const file = e.target.files[0];
      const res = await importDSVatTuDuTruBoSung(file);
      const contentType = res.headers["content-type"];
      if (contentType && !contentType.includes("application/json")) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Loi_nhap_danh_muc_vat_tu_vao_goi_thau_${moment().format("DDMMYYYY_HHmm")}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.error("Có lỗi trong file import, vui lòng xem file tải về.");
      } else {
        const text = await res.data.text();
        const data = JSON.parse(text);
        if (CODE.SUCCESS === data.code) {
          const newData = data.data.map((item: any) => ({
            ...item,
            unitOfMeasureName: item.unitofMeasureName || item.unitOfMeasureName,
            initialQuantity: item.totalQuantity,
            idFake: Math.random(),
          }));

          const currentList = Array.isArray(lstVatTu) ? [...lstVatTu] : [];
          if (currentList.length === 0) {
            setLstVatTu(newData);
            toast.success(`Import thành công ${newData.length} thuốc/vật tư`);
          } else {
            let skippedUsedCount = 0;
            let updatedCount = 0;
            let addedCount = 0;

            const normalize = (value: any) => String(value ?? "").trim().toLowerCase();

            const isSameItem = (existing: any, incoming: any) => {
              // 1. Có itemId ở cả hai bên thì chỉ so theo itemId, không fallback sang mã/tên
              const existingItemId = normalize(existing?.itemId || existing?.item?.id);
              const incomingItemId = normalize(incoming?.itemId || incoming?.item?.id);
              if (existingItemId && incomingItemId) {
                return existingItemId === incomingItemId;
              }

              // 2. So khớp theo Mã thuốc bệnh viện (hospitalMedicineCode)
              const h1 = normalize(existing?.hospitalMedicineCode || existing?.item?.hospitalMedicineCode);
              const h2 = normalize(incoming?.hospitalMedicineCode || incoming?.item?.hospitalMedicineCode);
              if (h1 && h2) {
                return h1 === h2;
              }

              // 3. So khớp theo Mã thuốc thông tư (code)
              const c1 = normalize(existing?.code || existing?.item?.code);
              const c2 = normalize(incoming?.code || incoming?.item?.code);
              if (c1 && c2) {
                return c1 === c2;
              }

              // 4. So khớp theo Tên thuốc (name)
              const n1 = normalize(existing?.name || existing?.item?.name || existing?.nameVT);
              const n2 = normalize(incoming?.name || incoming?.item?.name || incoming?.nameVT);
              return Boolean(n1 && n2 && n1 === n2);
            };

            const isMatch = (existing: any, incoming: any) => isSameItem(existing, incoming);

            const mergedList = [...currentList];

            newData.forEach((excelItem: any) => {
              const matchIndex = mergedList.findIndex((item) => isMatch(item, excelItem));
              if (matchIndex !== -1) {
                if (mergedList[matchIndex]?.isUsed) {
                  skippedUsedCount++;
                } else {
                  mergedList[matchIndex] = {
                    ...mergedList[matchIndex],
                    ...excelItem,
                    id: mergedList[matchIndex].id,
                    isUsed: false,
                    warehouseId: warehouseId || mergedList[matchIndex].warehouseId,
                  };
                  updatedCount++;
                }
              } else {
                mergedList.push({
                  ...excelItem,
                  id: null,
                  warehouseId: warehouseId,
                });
                addedCount++;
              }
            });

            setLstVatTu(mergedList);

            const details = [];
            if (addedCount > 0) details.push(`Thêm mới ${addedCount}`);
            if (updatedCount > 0) details.push(`Cập nhật ${updatedCount}`);
            if (skippedUsedCount > 0) details.push(`Bỏ qua ${skippedUsedCount} thuốc đã nhập kho`);
            const message = "Import thành công: " + (details.length > 0 ? details.join(", ") : "Không có thay đổi");

            if (skippedUsedCount > 0 && addedCount === 0 && updatedCount === 0) {
              toast.warning(message);
            } else {
              toast.success(message);
            }
          }
        } else {
          toast.error(data?.message || "Có lỗi xảy ra, vui lòng thử lại");
        }
      }
    } catch (error: any) {
      if (error.response && error.response.data instanceof Blob) {
        const contentType = error.response.headers["content-type"];
        if (contentType && !contentType.includes("application/json")) {
          const url = window.URL.createObjectURL(error.response.data);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Loi_nhap_danh_muc_vat_tu_vao_goi_thau_${moment().format("DDMMYYYY_HHmm")}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.error("Có lỗi trong file import, vui lòng xem file tải về.");
        } else {
          try {
            const text = await error.response.data.text();
            const data = JSON.parse(text);
            toast.error(data?.message || "Có lỗi xảy ra");
          } catch(err) {
            toast.error("Có lỗi xảy ra");
          }
        }
      } else {
        toast.error("Có lỗi xảy ra, vui lòng thử lại");
      }
      console.error(error);
    } finally {
      setIsLoading(false);
      e.target.value = null;
    }
  };


  return (
    <Formik
      enableReinitialize
      initialValues={{ [type?.description?.formVatTu]: initValues }}
      validationSchema={generateValidationSchema(
        listJsonGenerate,
        type?.description?.formVatTu
      )}
      onSubmit={handleAddItem}
    >
      {({
        values,
        touched,
        errors,
        setValues,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => (
        <Form onSubmit={handleSubmit}>
          <div className="spaces px-10">
            <GenerateFormComponent
              isView={isView}
              listFieldAuto={listJsonGenerate}
              handleChange={handleChange}
              setValues={setValues}
              values={values}
              touched={touched}
              errors={errors}
              setFieldValue={setFieldValue}
              modelID={type?.description?.formVatTu}
              isNhapKho={true}
              customComponent={DynamicComponent}
              warehouseId={warehouseId}
              propsRenderButton={(data: any) =>
                setInitValues({ ...initValues, ...data })
              }
              DynamicClass={"pl-20"}
              customProps={{
                labelProduct: labelProduct,
              }}
            />
            <HandleValues modelID={type?.description?.formVatTu} />
          </div>

          <div className=" d-flex w-100 spaces py-6 pl-5 mt-10">
            {!isView && <>
              <div>
                <Button
                  onClick={(e) => handleSubmit()}
                  className="btn btn-fill min-w-120px spaces"
                >
                  {indexRowEdit === null ? `Thêm ${labelProduct}` : `Cập nhật ${labelProduct}`}
                </Button>
              </div>
              <Button className="btn btn-fill position-relative spaces min-w-90px ml-4" >
                Import danh sách
                <TextField
                  className="btn-file"
                  type="file"
                  name="fileDSVatTuDuTru"
                  onChange={(e: any) => handleSelectFileImport(e)}
                />
              </Button>
              <Button className="btn-fill spaces min-w-90px btn btn-primary ml-4" onClick={handleDownloadSampleImport}>
                Tải mẫu nhập Excel
              </Button>
            </>
            }
          </div>
          <OctTable
            id="table-vat-tu-danh-muc"
            className="custom-header-table spaces h-400"
            columns={columns}
            data={lstVatTu}
            noToolbar
            clearToolbar
            noPagination
            fixedColumnsCount={-1}
          />
        </Form>
      )}
    </Formik>
  );
};

export default FormVatTuDanhMuc;
